/**
 * The shell's view on remote support requests (Fernwartung): which wait
 * for the admin's decision, which session runs. One store for the approval
 * dialog and the admins' banner.
 *
 * - **Who sees it.** Userinfo says: `support_approver` is true for an active
 *   holder of `master.support.approve` in a session that is nobody else's work
 *   (not an impersonation, not a support session). Without it nothing is
 *   asked, whatever pushes arrive — no probing of the support routes. A refusal of
 *   the door after all (403: the key was taken away; 503 `edge_unavailable`: a
 *   master without the edge link) is final for this page. An unreachable door
 *   is not a verdict: the store stays quiet and asks again with the next poll.
 * - **Live.** Master tells the holders about a request with a notification of
 *   its own type (`master.support_request`) and takes it back when the request
 *   is gone; the shell's realtime channel `master` carries it. Every such
 *   push — and every deletion — reads the list again. A slow poll and the
 *   clock (a decision window or a session that runs out) cover a missed push.
 * - A request closed with „x" is *dismissed*: it stays open on the server, the
 *   dialog leaves it alone until the page is reloaded.
 */
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useNxSocket } from '@blpde/connect';
import { useShellUser } from '@/auth';
import {
  approveSupportRequest,
  denySupportRequest,
  endSupportRequest,
  fetchSupportRequests,
  isNotAnApprover,
  isSupportUnavailable,
  supportErrorKey,
  type SupportRequest,
} from './supportApi';

/** The slowest the list is read again while an approver is signed in. */
const POLL_MS = 60_000;

const requests = shallowRef<SupportRequest[]>([]);
const { user } = useShellUser();
/** The door refused although userinfo called the session an approver: no more questions. */
const refused = ref(false);
/** Userinfo's `support_approver`, and the door has not said otherwise. */
const approver = computed(() => user.value?.supportApprover === true && !refused.value);
const now = ref(Date.now());
const dismissed = ref<ReadonlySet<string>>(new Set());
const busy = ref(false);
const error = ref<string | null>(null);
/** Why the last „Beenden" did not work (an i18n key) and for which request; shown at the banner, not in the dialog. */
const endFailure = ref<{ id: string; key: string } | null>(null);

let consumers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;
let reading: Promise<void> | null = null;
let readAgain = false;

function time(value: string | undefined): number {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : NaN;
}

const pending = computed(() =>
  requests.value.filter((r) => r.state === 'requested' && time(r.expiresAt) > now.value)
);

const active = computed(
  () =>
    requests.value.find(
      (r) => r.state === 'approved' && r.redeemedAt && !r.endedAt && time(r.endsAt) > now.value
    ) ?? null
);

/** The request a notification click asked for: shown ahead of the others. */
const focused = ref<string | null>(null);

/** The failure of „Beenden" for the session that runs now: a failure belongs to its request. */
const endError = computed(() =>
  endFailure.value && endFailure.value.id === active.value?.id ? endFailure.value.key : null
);

const current = computed(
  () =>
    pending.value.find((r) => r.id === focused.value) ??
    pending.value.find((r) => !dismissed.value.has(r.id)) ??
    null
);

function schedule(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  if (consumers === 0 || !approver.value) return;
  const boundaries = [
    ...pending.value.map((r) => time(r.expiresAt)),
    ...(active.value ? [time(active.value.endsAt)] : []),
  ].filter((at) => at > now.value);
  const next = Math.min(POLL_MS, ...boundaries.map((at) => at - now.value));
  timer = setTimeout(
    () => {
      // The clock and the next timer never wait for the network: a poll that
      // hangs must not keep a request's dialog open past its deadline.
      now.value = Date.now();
      schedule();
      void refresh();
    },
    Math.max(0, next)
  );
}

async function readOnce(): Promise<void> {
  try {
    requests.value = await fetchSupportRequests();
  } catch (err) {
    if (isNotAnApprover(err) || isSupportUnavailable(err)) {
      refused.value = true;
      requests.value = [];
    }
  }
  now.value = Date.now();
  // The request the failure belongs to ended or went: so did the failure.
  if (endFailure.value && endFailure.value.id !== active.value?.id) endFailure.value = null;
}

/** Reads the list again; a read that is on its way is followed by one more. */
async function refresh(): Promise<void> {
  if (!approver.value) return;
  if (reading) {
    readAgain = true;
    return reading;
  }
  reading = (async () => {
    do {
      readAgain = false;
      await readOnce();
    } while (readAgain && approver.value);
  })().finally(() => {
    reading = null;
    schedule();
  });
  return reading;
}

function onPush(env: { evt?: string; msg?: unknown } | undefined): void {
  const evt = env?.evt ?? '';
  if (!evt.startsWith('notification.')) return;
  const type = (env?.msg as { type?: unknown } | undefined)?.type;
  // A new or changed notification of another kind says nothing about requests.
  if (typeof type === 'string' && !type.startsWith('master.support')) return;
  void refresh();
}

function onVisible(): void {
  if (document.visibilityState === 'visible') void refresh();
}

async function decide(
  act: () => Promise<void>,
  fail: (key: string | null) => void = (key) => (error.value = key)
): Promise<boolean> {
  if (busy.value) return false;
  busy.value = true;
  fail(null);
  try {
    await act();
    return true;
  } catch (err) {
    fail(supportErrorKey(err));
    return false;
  } finally {
    busy.value = false;
    await refresh();
  }
}

/**
 * Shows one request, also one that was closed with „x" (a click on its
 * notification, which the shell handles itself — no app is involved). Reads the
 * list first; false when the request is no longer open — decided, expired,
 * unknown — or the session is no approver.
 */
export async function showSupportRequest(id: string): Promise<boolean> {
  if (!approver.value) return false;
  await refresh();
  if (!pending.value.some((r) => r.id === id)) return false;
  dismissed.value = new Set([...dismissed.value].filter((other) => other !== id));
  focused.value = id;
  return true;
}

/** Test seam: forget everything the store holds. */
export function resetSupportRequests(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  requests.value = [];
  refused.value = false;
  dismissed.value = new Set();
  focused.value = null;
  busy.value = false;
  error.value = null;
  endFailure.value = null;
  reading = null;
  readAgain = false;
  unsubscribe?.();
  unsubscribe = null;
  consumers = 0;
}

export function useSupportRequests() {
  const socket = useNxSocket();

  // Userinfo arrives after the first mount, and may say it again later.
  watch(approver, (now) => {
    if (consumers === 0) return;
    if (now) void refresh();
    else {
      requests.value = [];
      schedule();
    }
  });

  onMounted(() => {
    consumers += 1;
    if (consumers === 1) {
      unsubscribe =
        socket
          ?.channel('master')
          ?.on('event', (env: { evt?: string; msg?: unknown }) => onPush(env)) ?? null;
      document.addEventListener('visibilitychange', onVisible);
      void refresh();
    }
  });

  onBeforeUnmount(() => {
    consumers = Math.max(0, consumers - 1);
    if (consumers > 0) return;
    unsubscribe?.();
    unsubscribe = null;
    document.removeEventListener('visibilitychange', onVisible);
    if (timer !== null) clearTimeout(timer);
    timer = null;
  });

  return {
    /** Userinfo calls this session an approver (and the door has not refused it). */
    isApprover: approver,
    /** Requests waiting for a decision, oldest first. */
    pending,
    /** The one the dialog shows: the oldest waiting request that was not dismissed. */
    current,
    /** The remote support session that runs now, if any. */
    active,
    busy: computed(() => busy.value),
    /** The i18n key of the last refused decision, else null. */
    error: computed(() => error.value),
    /** Why the last „Beenden" failed (an i18n key), else null: the session goes on. */
    endError,
    refresh,
    approve: (id: string, minutes: number) => decide(() => approveSupportRequest(id, minutes)),
    deny: (id: string) => decide(() => denySupportRequest(id)),
    end: (id: string) =>
      decide(
        () => endSupportRequest(id),
        (key) => (endFailure.value = key === null ? null : { id, key })
      ),
    showRequest: showSupportRequest,
    /** Closes a request's dialog without deciding; it stays open on the server. */
    dismiss(id: string): void {
      dismissed.value = new Set([...dismissed.value, id]);
      if (focused.value === id) focused.value = null;
    },
    clearError(): void {
      error.value = null;
    },
  };
}
